import { TrendingDown, TrendingUp } from 'lucide-react';
import { LoadingSpinner } from 'components';
import { Chip } from 'common/components';

import styles from '../agentAnalytics.module.scss';

const trendingIcon = (trend: number | undefined) => {
    if (!trend) return null;
    if (trend > 0) {
        return <TrendingUp className='text-success' size={16} />;
    }

    return <TrendingDown className='text-error' size={16} />;
};

const formatToInteger = (value: number | undefined) => {
    return Number.isInteger(value) ? value : value?.toFixed(2);
};

export const formatValue = (value: number | undefined, isPercentage: boolean) => {
    if (isPercentage) {
        return `${formatToInteger((value || 0) * 100)}%`;
    }
    return value?.toString();
};

const TREND_VIEW_HEIGHT = 36;

const invertTrendPathY = (d: string, h: number = TREND_VIEW_HEIGHT) =>
    d.replace(
        /([ML]\s*)(-?(?:\d*\.?\d+)(?:e[-+]?\d+)?),(-?(?:\d*\.?\d+)(?:e[-+]?\d+)?)/gi,
        (_m, prefix: string, x: string, y: string) => `${prefix}${x},${h - parseFloat(y)}`
    );

const TREND_UP_LINE_PATHS: Record<0 | 1 | 2, string> = {
    0: 'M0,23 L10,25.769710022305976 L20,23.846473348203986 L28.999999999999996,21.61618337050996 L36,15.385893392815937 L50,19.53942004461195 L59.99999999999999,13.30913006691793 L70,11.232366741019923 L80,21.155603415121913 L90,19.002076763325896 L100,17.078840089223906 L110,20.078840089223906',
    1: 'M0,28 L10,23.69294669640797 L19,27.846473348203986 L29.999999999999996,17.462656718713944 L34,11.232366741019923 L38,19.53942004461195 L45.99999999999999,13.30913006691793 L55,7.078840089223906 L70,13.155603415121913 L85,17.002076763325896 L86,19.002076763325896 L110,11.002076763325896',
    2: 'M0,32 L10,25.251687078230443 L20,18.503374156460886 L29.999999999999996,25.251687078230443 L40,18.503374156460886 L50,11.755061234691329 L59.99999999999999,18.503374156460886 L70,11.755061234691329 L80,11.755061234691329 L90,5.006748312921772 L100,11.755061234691329 L110,5.006748312921772',
};

/** Map |trend| % to shape intensity: mild / medium / strong */
const getTrendLineVariant = (trend: number): 0 | 1 | 2 => {
    const m = Math.abs(trend);
    if (m < 33) return 0;
    if (m < 66) return 1;
    return 2;
};

const TrendingLineGraphic = ({
    trend = 0,
    isUp,
    isTrendingDefined,
}: {
    trend?: number;
    isUp: boolean;
    isTrendingDefined: boolean;
}) => {
    const idx = isTrendingDefined ? getTrendLineVariant(trend) : 2;

    const baseD = TREND_UP_LINE_PATHS[idx];
    const lineD = isUp || !isTrendingDefined ? baseD : invertTrendPathY(baseD, TREND_VIEW_HEIGHT);

    return (
        <div
            className={`${styles.trendingLine} ${isUp ? styles.trendingLineUp : styles.trendingLineDown} ${
                isTrendingDefined ? '' : styles.trendingLineUndefined
            }`}
            aria-hidden
        >
            <svg viewBox='0 0 110 36' width='100%' height='100%'>
                <path d={`${lineD} L110,36 L0,36 Z`} fill='currentColor' opacity={0.35} />
                <path
                    d={lineD}
                    fill='none'
                    stroke='currentColor'
                    strokeWidth={2.2}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
            </svg>
        </div>
    );
};

export const AgentAnalyticsItem = ({
    title,
    label,
    value,
    trend,
    isLoading = false,
    isPercentage = false,
}: {
    title: string;
    label?: string;
    value?: number;
    trend?: number;
    isLoading?: boolean;
    isPercentage?: boolean;
}) => {
    const isTrendingDefined = typeof trend === 'number' && typeof value === 'number';
    const isTrendingUp = isTrendingDefined && trend > 0;
    const isTrendingDown = isTrendingDefined && trend < 0;

    return (
        <div
            className={`${styles.agentAnalyticsItem} ${isTrendingUp ? styles.trendingUp : isTrendingDown ? styles.trendingDown : ''}`}
        >
            {isLoading && (
                <div>
                    <LoadingSpinner size={14} className={styles.loadingSpinner} />
                </div>
            )}
            <div className={styles.agentAnalyticsItemContent}>
                <div className='flex justify-between gap-xs'>
                    <h5 className=''>{title}</h5>

                    <Chip color={isTrendingUp ? 'success' : isTrendingDown ? 'error' : 'secondary'} size='xs'>
                        <p className='flex items-center gap-xs'>
                            {isTrendingDefined ? (
                                <>
                                    {trendingIcon(trend)} {formatToInteger(trend)}%
                                </>
                            ) : (
                                <span>N/A</span>
                            )}
                        </p>
                    </Chip>
                </div>
                <p className='text-3xl font-bold'>{formatValue(value, isPercentage)}</p>
                {label && <p className='text-sm text-gray-700'>{label}</p>}
            </div>
            <TrendingLineGraphic trend={trend} isUp={isTrendingUp} isTrendingDefined={isTrendingDefined} />
        </div>
    );
};
